import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Container,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useLocation } from "react-router-dom";

import logo from "../../assets/NC-Logo.png";

const Navigation = () => {
  const theme = useTheme();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // < 900px
  const isDesktop = useMediaQuery(theme.breakpoints.up("md")); // ≥ 900px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600px - 900px (за фини настройки)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // < 600px

  const navItems = [
    { label: "Карта", path: "/" },
    { label: "За проекта", path: "/about" },
    { label: "Доклад", path: "/report" },
    { label: "База данни", path: "/database" },
    { label: "Методология", path: "/methodology" },
    { label: "Общини", path: "/municipalities" },
    { label: "ЧЗВ", path: "/faq" },
    { label: "Контакт", path: "/contact" },
  ];

  // НОВИ бутони за Карта | Данни | Наръчник | Грантове (външни линкове)
  const externalNavItems = [
    {
      label: "ОСНОВЕН САЙТ",
      url: "https://narodnichitalishta.bg/",
      icon: "📊",
    },
    { label: "КАРТА", url: "https://karta.narodnichitalishta.bg/", icon: "🗺️" },
    {
      label: "НАРЪЧНИК",
      url: "https://naruchnik.narodnichitalishta.bg/",
      icon: "📘",
    },
    {
      label: "ГРАНТОВЕ",
      url: "https://grantove.narodnichitalishta.bg/",
      icon: "💰",
    },
  ];

  const handleDrawerToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Съкратени labels за малки екрани (600px - 900px)
  const getNavItemLabel = (label) => {
    if (isTablet) {
      const shortLabels = {
        "База данни читалища": "База данни",
        "Общински данни": "Общини",
        Методология: "Метод.",
      };
      return shortLabels[label] || label;
    }
    return label;
  };

  return (
    <>
      {/* НОВИЯТ БУТОН БАР - добавен в началото */}
      <Box
        sx={{
          background: "#edebe9",
          py: 0.5,
          borderBottom: "1px solid #edebe9",
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
            }}
          >
            {externalNavItems.map((item) => (
              <Button
                key={item.label}
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.text.grayLight,
                  textTransform: "none",
                  fontSize: {
                    xs: "0.55rem",
                    sm: "0.6rem",
                    md: "0.65rem",
                    lg: "0.7rem",
                  },
                  fontWeight: 600,
                  py: 0,
                  px: 1, // Добавяме хоризонтален падинг
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  overflow: "visible", // Променяме на visible
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

                  // Основен стил и hover за текста
                  "&:hover": {
                    color: theme.palette.custom.primaryOrange,
                    backgroundColor: "transparent",
                    transform: "translateY(-2px)",
                  },

                  // Анимирано подчертаване само под текста
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: "-2px",
                    left: "50%", // Центриране
                    width: "0%", // Започва с 0% ширина
                    height: "1px",
                    backgroundColor: theme.palette.custom.primaryOrange,
                    transform: "translateX(-50%)", // Центриране
                    transition: "width 0.4s cubic-bezier(0.25, 1, 0.5, 1)",
                    opacity: 1,
                  },

                  // При ховър - разширяване на линията
                  "&:hover::after": {
                    width: "100%", // Пълна ширина, но само в границите на текста
                  },

                  // Ефект при фокус
                  "&:focus-visible": {
                    outline: `1px solid ${theme.palette.custom.primaryOrange}`,
                    outlineOffset: "3px",
                    color: theme.palette.custom.primaryOrange,
                  },

                  // Ефект при клик
                  "&:active": {
                    transform: "translateY(0)",
                    transition: "transform 0.1s",
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ОСНОВЕН НАВИГАЦИОНЕН БАР */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: theme.palette.custom.primaryOrangeGradient,
          borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
        }}
      >
        <Container
          maxWidth="xl"
          disableGutters
          sx={{
            px: {
              xs: 1, // Mobile: 8px
              sm: 2, // Tablet: 16px
              md: 3, // 13" лаптоп: 24px (≥ 900px)
              lg: 4, // Desktop: 32px
            },
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              justifyContent: "space-between",
              minHeight: {
                xs: "56px", // Mobile
                sm: "60px", // Tablet
                md: "64px", // 13" лаптоп (≥ 900px)
                lg: "72px", // Desktop
              },
            }}
          >
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                height: {
                  xs: "35px", // Mobile
                  sm: "40px", // Tablet
                  md: "45px", // 13" лаптоп (≥ 900px)
                  lg: "55px", // Desktop
                },
                transition: "transform 0.3s, opacity 0.3s",
                "&:hover": {
                  transform: "scale(1.02)",
                  opacity: 0.9,
                },
              }}
            >
              <img
                src={logo}
                alt="Народни Читалища"
                style={{
                  height: "100%",
                  width: "auto",
                  maxWidth: {
                    xs: "110px", // Mobile
                    sm: "130px", // Tablet
                    md: "150px", // 13" лаптоп (≥ 900px)
                    lg: "200px", // Desktop
                  },
                  objectFit: "contain",
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Box>

            {/* Desktop Navigation - Показва се ОТ 900px (md) нагоре */}
            <Box
              sx={{
                display: {
                  xs: "none", // Mobile: скрито
                  sm: "none", // Tablet (600-900px): скрито
                  md: "flex", // 13" лаптоп (≥ 900px): показва се
                  lg: "flex", // Desktop: показва се
                },
                gap: {
                  md: 0.5, // 13" лаптоп (900-1200px): малък gap
                  lg: 1, // Desktop (>1200px): нормален gap
                },
                flexWrap: "nowrap",
                overflow: "hidden",
              }}
            >
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: "white",
                    textTransform: "none",
                    fontSize: {
                      md: "0.8rem", // 13" лаптоп (900-1200px): по-малък текст
                      lg: "0.85rem", // Desktop (1200-1536px): нормален текст
                      xl: "1rem", // Голям екран (>1536px): по-голям текст
                    },
                    fontWeight: location.pathname === item.path ? 600 : 500,
                    px: {
                      md: 1.2, // 13" лаптоп: малко padding
                      lg: 1.5, // Desktop: нормален padding
                      xl: 2, // Голям екран: повече padding
                    },
                    py: {
                      md: 0.8, // 13" лаптоп: по-малко
                      lg: 1, // Desktop: нормално
                      xl: 1.2, // Голям екран: повече
                    },
                    position: "relative",
                    borderRadius: 0,
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                    letterSpacing: { md: "-0.2px", lg: "normal" },
                    transition: "all 0.3s ease",
                    // Underline за active page
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: { md: 6, lg: 8, xl: 8 },
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: location.pathname === item.path ? "70%" : "0%",
                      height: "2px",
                      backgroundColor: "white",
                      transition: "width 0.3s ease",
                    },
                    // Hover underline
                    "&:hover": {
                      backgroundColor: "transparent",
                      "&::after": {
                        width: "70%",
                      },
                    },
                  }}
                >
                  {getNavItemLabel(item.label.toUpperCase())}
                </Button>
              ))}
            </Box>

            {/* Mobile Menu Button - Показва се САМО под 900px (md) */}
            <IconButton
              color="inherit"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{
                display: {
                  xs: "flex", // Mobile (<600px): показва се
                  sm: "flex", // Tablet (600-900px): показва се
                  md: "none", // 13" лаптоп (≥900px): СКРИТО
                  lg: "none", // Desktop: скрито
                  xl: "none", // Голям екран: скрито
                },
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer - Показва се САМО под 900px (md) */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: {
            xs: "block", // Mobile (<600px)
            sm: "block", // Tablet (600-900px)
            md: "none", // 13" лаптоп (≥900px): СКРИТО
            lg: "none", // Desktop: скрито
            xl: "none", // Голям екран: скрито
          },
          "& .MuiDrawer-paper": {
            width: {
              xs: "85%", // Mobile: 85% от ширината
              sm: 350, // Tablet: 350px
              md: 400, // 13" лаптоп (но няма да се показва)
            },
            maxWidth: "100vw",
            background: theme.palette.custom.primaryOrangeGradient,
            color: "white",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <Box sx={{ height: "40px" }}>
            <img
              src={logo}
              alt="Народни Читалища"
              style={{
                height: "100%",
                width: "auto",
                objectFit: "contain",
                filter: "brightness(0) invert(1)",
              }}
            />
          </Box>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.15)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Добавяне на външните линкове и в мобилното меню */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mb: 2,
            }}
          >
            {externalNavItems.map((item) => (
              <Button
                key={item.label}
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  textTransform: "none",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  borderRadius: "4px",
                  minWidth: "auto",
                  flex: "1 0 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                  },
                }}
              >
                {/* <span style={{ fontSize: "0.9em" }}>{item.icon}</span> */}
                {item.label}
              </Button>
            ))}
          </Box>
        </Box>

        <List sx={{ pt: 0, px: 2 }}>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={handleDrawerToggle}
                selected={location.pathname === item.path}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: 1,
                  borderLeft:
                    location.pathname === item.path
                      ? "3px solid white"
                      : "3px solid transparent",
                  transition: "all 0.3s",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderLeft: "3px solid rgba(255, 255, 255, 0.5)",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: location.pathname === item.path ? 600 : 500,
                    fontSize: "0.95rem",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Navigation;
